import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import useUserCompany from "../../../hooks/useUserCompany";
import { useSelector } from "react-redux";
import LoadingSpinner from "../../LoadingSpinner";
import { getBehalfOf } from "../../../api/systemUserApi";
import SearchDropdown from "../../input/SearchDropdown";
import {
  getBulkDropdownData,
  getOtherRefSearchDropdown,
} from "../../../api/keyvalue";
import { Card, CardContent } from "@mui/material";
import { AlertCircle, Type, User } from "react-feather";
import { FORMS, inputFieldProfile, MODULES } from "../../../const";
import Tab from "../../Tab";
import Input from "../../input/Input";
import Button from "../../input/Button";
import { errorMessageToast, isNumber, toFloatOrNull } from "../../../helper";
import { toast } from "react-toastify";
import {
  createUserSupplierNegotiation,
  updateUserSupplierNegotiation,
} from "../../../api/userSupplierNegotiationApi";
import Dropdown from "../../input/Dropdown";
import InfoList from "../../InfoList";
import { formatFromJsonDate } from "../../../dateConfig";
import Modal from "../../Modal";

const UserSupplierNegotiation = forwardRef(
  ({ closeDrawer, editMode, currentValues }, ref) => {
    const [isLoading, setIsLoading] = useState(false);
    const company = useUserCompany();
    const user = useSelector((state) => state.user);

    const defaultState = {
      usnId: null,
      usnCode: null,
      usnReference: null,
      usnCompany: company.comId,
      usnUser: user.username,
      usnCustomer: null,
      usnCustomerName: "",
      usnMinAmount: null,
      usnMaxAmount: null,
      usnMinQty: null,
      usnMaxQty: null,
      usnStatus: 2,
      usnActive: 1,
      author: user.username,
    };
    const [auditInfo, setAuditInfo] = useState({});

    const [userSupplierNegotiation, setUserSupplierNegotiation] =
      useState(defaultState);

    const [isEditMode, setIsEditMode] = useState(editMode);
    const [searchFieldReset, setSearchFieldReset] = useState(false);
    const [currentErrors, setCurrentErrors] = useState({});
    const [tab, setTab] = useState(1);

    const deleteWarningRef = useRef();
      const [showDeleteWarning, setShowDeleteWarning] = useState(false);
      const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

    const [initialActive, setInitialActive] = useState(
      editMode ? currentValues?.usnActive : 1,
    );

    const [statusOptions, setStatusOptions] = useState([]);
    const [activeOption, setActiveOption] = useState([]);

    useEffect(() => {
      (async () => {
        setIsLoading(true);
        const { active, status } = await getBulkDropdownData([
          { key: "active" },
          { key: "status" },
        ]);
        setActiveOption(active);
        setStatusOptions(status);
        setIsLoading(false);
      })();
    }, []);

    useEffect(() => {
      setIsEditMode(editMode);
    }, [editMode]);

    useEffect(() => {
      setUserSupplierNegotiation((prevState) => ({
        ...prevState,
        usnUser: user.username,
        author: user.username,
      }));
    }, [user.username]);

    useEffect(() => {
      if (isEditMode) {
        setUserSupplierNegotiation(currentValues);
        setAuditInfo({
          createdDate: currentValues.createdDate,
          createdBy: currentValues.createdBy,
          lastModifiedBy: currentValues.lastModifiedBy,
          lastModifiedDate: currentValues.lastModifiedDate,
        });
      }
    }, [editMode, currentValues]);

    const isPairValid = (pair) => {
      if (!isNumber(pair.min) && !isNumber(pair.max)) {
        return -1;
      }

      if (!isNumber(pair.min)) {
        return -2;
      }
      if (!isNumber(pair.max)) {
        return -3;
      }

      return pair.max > pair.min ? 1 : -4;
    };

    const customerRef = useRef(null);

    const handleReset = () => {
      setUserSupplierNegotiation(defaultState);
      customerRef.current?.reset();
      setIsEditMode(false);
    };

    useImperativeHandle(ref, () => ({
      isDeletedActive: () =>
        userSupplierNegotiation.usnActive === 2 && initialActive !== 2,
      reset: handleReset,
    }));

    const submitHandler = async (e,bypassDeleteCheck = false) => {
      e.preventDefault();
      if (!bypassDeleteCheck &&  userSupplierNegotiation.usnActive === 2 && initialActive !== 2) {
            setPendingSubmitEvent(e);
            setShowDeleteWarning(true);
            deleteWarningRef.current?.showModal();
            return;
          }      
      setIsLoading(true);
      let hasErrors = false;
      if (!userSupplierNegotiation.usnUser) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnUser: "User is mandatory",
        }));
      }

      if (
        userSupplierNegotiation.usnCustomer === null ||
        userSupplierNegotiation.usnCustomer === undefined
      ) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnCustomer: "Customer is mandatory",
        }));
      }

      if (
        userSupplierNegotiation.usnStatus === null ||
        userSupplierNegotiation.usnStatus === undefined
      ) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnStatus: "Customer is mandatory",
        }));
      }

      if (
        userSupplierNegotiation.usnActive === null ||
        userSupplierNegotiation.usnActive === undefined
      ) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnActive: "Customer is mandatory",
        }));
      }

      const isAmountPairValid = isPairValid({
        min: toFloatOrNull(userSupplierNegotiation.usnMinAmount),
        max: toFloatOrNull(userSupplierNegotiation.usnMaxAmount),
      });

      const isQtyPairValid = isPairValid({
        min: toFloatOrNull(userSupplierNegotiation.usnMinQty),
        max: toFloatOrNull(userSupplierNegotiation.usnMaxQty),
      });

      if (isAmountPairValid === -1 && isQtyPairValid === -1) {
        toast.error("Amount range or Qty range is mandatory", {
          autoClose: 5000,
          containerId: "Main",
        });
        setIsLoading(false);
        return;
      }

      if (isAmountPairValid === -2) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMinAmount: "Min Amount cannot be empty",
        }));
      }

      if (isAmountPairValid === -3) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMaxAmount: "Max Amount cannot be empty",
        }));
      }

      if (isAmountPairValid === -4) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMaxAmount: "Max Amount cannot be less than min amount",
        }));
      }

      if (isQtyPairValid === -2) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMinQty: "Min Qty cannot be empty",
        }));
      }

      if (isQtyPairValid === -3) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMaxQty: "Max Qty cannot be empty",
        }));
      }

      if (isQtyPairValid === -4) {
        hasErrors = true;
        setCurrentErrors((prevState) => ({
          ...prevState,
          usnMaxAmount: "Max Qty cannot be less than min qty",
        }));
      }

      if (hasErrors) {
        setIsLoading(false);
        return;
      }

      try {
        if (isEditMode) {
          await updateUserSupplierNegotiation({
            ...userSupplierNegotiation,
            usnCompany: company?.comId,
            usnMinAmount: toFloatOrNull(userSupplierNegotiation.usnMinAmount),
            usnMaxAmount: toFloatOrNull(userSupplierNegotiation.usnMaxAmount),
            usnMinQty: toFloatOrNull(userSupplierNegotiation.usnMinQty),
            usnMaxQty: toFloatOrNull(userSupplierNegotiation.usnMaxQty),
            author: user.username,
            lastModifiedBy: user.username,
          });
        } else {
          await createUserSupplierNegotiation({
            ...userSupplierNegotiation,
            usnMinAmount: toFloatOrNull(userSupplierNegotiation.usnMinAmount),
            usnMaxAmount: toFloatOrNull(userSupplierNegotiation.usnMaxAmount),
            usnMinQty: toFloatOrNull(userSupplierNegotiation.usnMinQty),
            usnMaxQty: toFloatOrNull(userSupplierNegotiation.usnMaxQty),
            usnCompany: company?.comId,
            author: user.username,
          });
        }
        setCurrentErrors({});
        handleReset();
        closeDrawer();
      } catch (e) {
        errorMessageToast(e);
      }
      setIsLoading(false);
    };

    const deleteWarningModal = (
        <div style={{ padding: "20px" }}>
          <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <AlertCircle color="red" size="25" />
            <p>Warning</p>
          </h3>
          <p>Active is set to Deleted. Are you sure you want to proceed?</p>
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <Button
              label="Cancel"
              onClick={() => {
                deleteWarningRef.current?.close();
                setShowDeleteWarning(false);
                setPendingSubmitEvent(null);
              }}
            />
            <Button
              label="Proceed"
              className="ui red button"
              onClick={() => {
                deleteWarningRef.current?.close();
                setShowDeleteWarning(false);
                submitHandler(pendingSubmitEvent, true);
                setPendingSubmitEvent(null);
              }}
            />
          </div>
        </div>
      );

    return (
      <>
        <Card style={{ boxShadow: "none", height: "75vh", width: "60vw" }}>
          <CardContent style={{ boxShadow: "none" }}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <>
              <form
                className="ui form"
                style={{ overflowY: "auto" }}
                onSubmit={submitHandler}
              >
                <div className="ui top attached tabular menu">
                  <Tab
                    Icon={Type}
                    onClick={() => {
                      setTab(1);
                    }}
                    active={tab === 1}
                    label="Stakeholder Types"
                  />
                </div>
                <div
                  className="ui bottom attached active tab segment"
                  style={{ display: tab === 1 ? "block" : "none" }}
                >
                  <div className="ui segment">
                    <div className="ui dividing header">
                      User Customer Selection
                    </div>
                    <div className="two fields">
                      <SearchDropdown
                        key={1}
                        threshold={1}
                        required={true}
                        multiGrid={true}
                        returnFullObject={true}
                        multiGridCurrentValue={userSupplierNegotiation.usnUser}
                        editMode={editMode}
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        api={getBehalfOf}
                        apiDependency={{
                          customer: null,
                          shipper: null,
                        }}
                        onResultSelect={(data) =>
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnUser: data.usrUsername,
                          }))
                        }
                        error={currentErrors.usnUser}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnUser: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        label="User"
                        disabled={editMode}
                      />
                      <SearchDropdown
                        key={1}
                        required={true}
                        editMode={editMode}
                        disabled={editMode}
                        multiGrid={true}
                        multiGridCurrentValue={
                          userSupplierNegotiation.usnCustomerName
                        }
                        reset={searchFieldReset}
                        setReset={setSearchFieldReset}
                        returnFullObject={true}
                        clearable={true}
                        onClear={() => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnCustomer: null,
                            usnCustomerName: "",
                          }));
                        }}
                        onResultSelect={(data) =>
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnCustomer: data.value,
                            usnCustomerName: data.title,
                          }))
                        }
                        api={getOtherRefSearchDropdown}
                        apiDependency={{
                          type: "stakeholder_type",
                          value: "Customer",
                        }}
                        fieldInfo={{
                          tableName: "masterdata.tms_m_stakeholder",
                          primaryKey: "stk_id",
                          fieldName: "stk_name",
                          id: userSupplierNegotiation.usnCustomer,
                        }}
                        error={currentErrors.usnCustomer}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnCustomer: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        label="Customer"
                      />
                    </div>
                    <div className="two fields">
                      <Dropdown
                        clearable={false}
                        required={true}
                        label="Status"
                        error={currentErrors.usnStatus}
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnStatus: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Status"
                        options={statusOptions}
                        style={{ height: "20px" }}
                        value={userSupplierNegotiation.usnStatus}
                        onChange={(e, data) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnStatus: data.value,
                          }));
                        }}
                      />
                      <Dropdown
                        clearable={false}
                        required={true}
                        label="Active"
                        error={currentErrors.usnActive}
                        editMode={editMode}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnActive: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        placeholder="Active"
                        options={activeOption}
                        style={{ height: "20px" }}
                        value={userSupplierNegotiation.usnActive}
                        onChange={(e, data) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnActive: data.value,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div className="ui segment">
                    <div className="ui dividing header">Range Selection</div>
                    <div className="two fields">
                      <Input
                        label="Min Amount"
                        placeholder="Min Amount"
                        error={currentErrors.usnMinAmount}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnMinAmount: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        profile={inputFieldProfile.DECIMAL}
                        value={userSupplierNegotiation.usnMinAmount}
                        onChange={(e) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnMinAmount: e.target.value,
                          }));
                        }}
                      />
                      <Input
                        label="Max Amount"
                        placeholder="Max Amount"
                        error={currentErrors.usnMaxAmount}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnMaxAmount: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        profile={inputFieldProfile.DECIMAL}
                        value={userSupplierNegotiation.usnMaxAmount}
                        onChange={(e) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnMaxAmount: e.target.value,
                          }));
                        }}
                      />
                    </div>
                    <div className="two fields">
                      <Input
                        label="Min Qty"
                        placeholder="Min Qty"
                        error={currentErrors.usnMinQty}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnMinQty: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        profile={inputFieldProfile.DECIMAL}
                        value={userSupplierNegotiation.usnMinQty}
                        onChange={(e) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnMinQty: e.target.value,
                          }));
                        }}
                      />
                      <Input
                        label="Max Qty"
                        placeholder="Max Qty"
                        error={currentErrors.usnMaxQty}
                        resetError={() =>
                          setCurrentErrors((prevState) => {
                            const { usnMaxQty: _, ...rest } = prevState;
                            return rest;
                          })
                        }
                        profile={inputFieldProfile.DECIMAL}
                        value={userSupplierNegotiation.usnMaxQty}
                        onChange={(e) => {
                          setUserSupplierNegotiation((prevState) => ({
                            ...prevState,
                            usnMaxQty: e.target.value,
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isLoading}
                      create={!isEditMode}
                    />
                  </div>
                </div>
              </form>
               <Modal
                            ref={deleteWarningRef}
                            closeButton={false}
                            minWidth={"300px"}
                          >
                            {showDeleteWarning && deleteWarningModal}
                          </Modal>
              </>
            )}
          </CardContent>
        </Card>
        {editMode && (
          <InfoList
            title="User Information"
            content={[
              {
                title: "Created By",
                value: auditInfo.createdBy ? auditInfo.createdBy : "N/A",
              },
              {
                title: "Created Date",
                value: auditInfo.createdDate
                  ? formatFromJsonDate(auditInfo.createdDate)
                  : "N/A",
              },
              {
                title: "Last Modified By",
                value: auditInfo.lastModifiedBy
                  ? auditInfo.lastModifiedBy
                  : "N/A",
              },
              {
                title: "Last Modified Date",
                value: auditInfo.lastModifiedDate
                  ? formatFromJsonDate(auditInfo.lastModifiedDate)
                  : "N/A",
              },
            ]}
            Icon={User}
          />
        )}
      </>
    );
  },
);

export default UserSupplierNegotiation;
